import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { blogPosts, categories } from '../data/blogPosts';
import { Clock, ArrowRight, Search, Calendar, ChevronRight } from 'lucide-react';
import { AnimatedText } from '../components/AnimatedText';
import gridPattern from '../grid-pattern.svg';

export const BlogPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();

  // Parallax effect for hero
  const heroY = useTransform(scrollY, [0, 500], [0, 150]);
  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const menuItems = [
    { label: 'Features', href: '/#features' },
    { label: 'Blog', href: '/blog' },
    { label: 'Press', href: '/press' },
    { label: 'Pitch', href: '/pitch' },
    { label: 'Enterprise API', href: '/enterprise-api' }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get most recent post for featured section
  const featuredPost = [...blogPosts]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];

  // Filter posts
  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = searchQuery.trim() === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Remove featured post from grid if it's in the list (unless searching/filtering)
  const gridPosts = (selectedCategory === 'All' && !searchQuery)
    ? filteredPosts.filter(p => p.id !== featuredPost.id)
    : filteredPosts;

  return (
    <>
      <Helmet>
        <title>Cura Blog – Insights on Digital Health & Senior Care</title>
        <meta name="description" content="Explore expert insights on healthcare accessibility, AI in medicine, and senior wellness. Stay informed with the latest from the Cura team ✓" />
        <link rel="canonical" href="https://mycura.org/blog" />
        <meta property="og:title" content="Cura Blog – Insights on Digital Health & Senior Care" />
        <meta property="og:description" content="Explore expert insights on healthcare accessibility, AI in medicine, and senior wellness. Stay informed with the latest from the Cura team." />
        <meta property="og:url" content="https://mycura.org/blog" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cura Blog – Insights on Digital Health & Senior Care" />
        <meta name="twitter:description" content="Explore expert insights on healthcare accessibility, AI in medicine, and senior wellness. Stay informed with the latest from the Cura team." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Cura Blog",
            "url": "https://mycura.org/blog",
            "isPartOf": { "@type": "WebSite", "name": "Cura", "url": "https://mycura.org" }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mycura.org/" },
              { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://mycura.org/blog" }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-slate-50 selection:bg-blue-200 selection:text-blue-900">
        <Navigation
          menuItems={menuItems}
          scrollToSection={scrollToSection}
          scrollToTop={scrollToTop}
          forceWhiteBackground={true}
        />

        <main className="relative pt-32 pb-24 overflow-hidden">
          {/* Background Decor */}
          <div className="absolute top-0 left-0 right-0 h-[600px] bg-gradient-to-b from-blue-50 to-transparent -z-10" />
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none bg-repeat"
            style={{ backgroundImage: `url(${gridPattern})` }}
          />
          <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-3xl -z-10 translate-x-1/3" />
          <div className="absolute top-40 left-0 w-[400px] h-[400px] bg-purple-100/40 rounded-full blur-3xl -z-10 -translate-x-1/3" />

          {/* Header Section */}
          <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-16 md:mb-24">
            <motion.div
              style={{ y: heroY, opacity: heroOpacity }}
              className="text-center max-w-4xl mx-auto pt-12"
            >
              <div className="mb-6">
                <motion.span
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="inline-block py-1 px-3 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold tracking-wide"
                >
                  The Cura Journal
                </motion.span>
              </div>

              <h1 className="text-5xl md:text-7xl text-slate-900 mb-6 leading-tight flex flex-col md:block">
                <AnimatedText delay={0.1}>Insights for</AnimatedText>{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  <AnimatedText delay={0.2}>Better Care</AnimatedText>
                </span>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed"
              >
                Exploring the future of healthcare accessibility, digital innovation, and senior wellness.
              </motion.p>
            </motion.div>
          </section>

          {/* Search and Filter Section */}
          <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-16 sticky top-20 z-30">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/50 p-4 md:p-6"
            >
              <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
                {/* Categories */}
                <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto no-scrollbar">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${selectedCategory === category
                        ? 'bg-slate-900 text-white shadow-md transform scale-105'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                        }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>

                {/* Search */}
                <div className="relative w-full md:w-80" ref={searchRef}>
                  <div className={`relative transition-all duration-300 ${isSearchFocused ? 'scale-105' : ''}`}>
                    <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors ${isSearchFocused ? 'text-blue-600' : 'text-slate-400'}`} />
                    <input
                      type="text"
                      placeholder="Search articles..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setIsSearchFocused(false)}
                      className="w-full pl-12 pr-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none transition-all duration-300 text-slate-900 placeholder-slate-400"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </section>

          {/* Featured Article (Only show if no search/filter active or if it matches) */}
          {selectedCategory === 'All' && !searchQuery && (
            <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-20">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <Link to={`/blog/${featuredPost.slug}`} className="group block relative">
                  <div className="relative rounded-[2rem] overflow-hidden bg-slate-900 aspect-[16/10] md:aspect-[21/9] shadow-2xl">
                    <img
                      src={featuredPost.image}
                      alt={featuredPost.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-80 group-hover:opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent hidden md:block" />

                    <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16 text-white hidden md:block">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        className="max-w-4xl"
                      >
                        <div className="flex items-center gap-4 mb-6">
                          <span className="px-4 py-1.5 bg-blue-600 text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-lg shadow-blue-900/20">
                            Featured
                          </span>
                          <span className="flex items-center gap-2 text-slate-300 text-sm font-medium">
                            <Calendar className="w-4 h-4" />
                            {new Date(featuredPost.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>

                        <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight group-hover:text-blue-200 transition-colors duration-300">
                          {featuredPost.title}
                        </h2>

                        <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl line-clamp-2 md:line-clamp-3">
                          {featuredPost.excerpt}
                        </p>

                        <span className="inline-flex items-center gap-2 text-blue-400 font-bold group-hover:text-blue-300 transition-colors">
                          Read Article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </motion.div>
                    </div>
                  </div>

                  {/* Mobile Text Content (Below Image) */}
                  <div className="mt-6 md:hidden">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider rounded-full">
                        Featured
                      </span>
                      <span className="flex items-center gap-2 text-slate-500 text-sm font-medium">
                        <Calendar className="w-4 h-4" />
                        {new Date(featuredPost.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>

                    <h2 className="text-2xl font-bold mb-4 leading-tight text-slate-900">
                      {featuredPost.title}
                    </h2>

                    <p className="text-base text-slate-600 mb-6 line-clamp-3">
                      {featuredPost.excerpt}
                    </p>

                    <span className="inline-flex items-center gap-2 text-blue-600 font-bold">
                      Read Article <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            </section>
          )}

          {/* Articles Grid */}
          <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            {gridPosts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {gridPosts.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Link
                      to={`/blog/${post.slug}`}
                      className="group flex flex-col h-full bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-slate-100"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-white/90 backdrop-blur-md rounded-full text-xs font-bold text-slate-900 shadow-sm">
                            {post.category}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col flex-grow p-6">
                        <div className="flex items-center gap-3 text-xs text-slate-500 mb-4">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {post.readTime}
                          </span>
                        </div>

                        <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                          {post.title}
                        </h3>

                        <p className="text-slate-600 text-sm leading-relaxed mb-6 line-clamp-3 flex-grow">
                          {post.excerpt}
                        </p>

                        <div className="flex items-center text-blue-600 text-sm font-semibold group-hover:gap-2 transition-all duration-300">
                          Read More <ChevronRight className="w-4 h-4 ml-1" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-24"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-6">
                  <Search className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No articles found</h3>
                <p className="text-slate-500">Try adjusting your search or category filter</p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('All');
                  }}
                  className="mt-6 text-blue-600 font-medium hover:text-blue-700 hover:underline"
                >
                  Clear all filters
                </button>
              </motion.div>
            )}
          </section>

          {/* Newsletter Section */}
          <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mt-24">
            <div className="relative rounded-3xl overflow-hidden bg-slate-900 px-6 py-16 md:px-16 text-center">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent" />

              <div className="relative z-10 max-w-2xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Stay updated with Cura
                </h2>
                <p className="text-slate-300 mb-8 text-lg">
                  Get the latest insights on digital health and senior care delivered straight to your inbox.
                </p>

                <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" onSubmit={(e) => e.preventDefault()}>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-grow px-5 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 backdrop-blur-sm"
                  />
                  <button className="px-8 py-3.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-500 transition-colors shadow-lg shadow-blue-900/20">
                    Subscribe
                  </button>
                </form>
                <p className="text-slate-400 text-xs mt-4">
                  We care about your data in our <Link to="/privacy" className="underline hover:text-white">privacy policy</Link>.
                </p>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};
